import { FC, useEffect, useState } from 'react';
import { Box, Button, Grid } from '@mui/material';
import {
  DataGrid,
  GridCellEditCommitParams,
  GridCellParams,
  GridCellValue,
  GridColDef,
  GridPreProcessEditCellProps,
} from '@mui/x-data-grid';
import { LoadingButton } from '@mui/lab';
import { LoadingSpinner } from '@components';
import StateBasedPricingCsvUploadModal from '../StateBasedPricingCsvUploadModal/StateBasedPricingCsvUploadModal';
import { StateBasedPricingCsvDownloadModal } from '@pages/Products/StateBasedPricingCsvDownloadModal/StateBasedPricingCsvDownloadModal';
import { toCurrency } from '@helpers';
import { useAppDispatch } from '@hooks/redux-custom';
import { JimboResponse, PriceListRecord, PriceListWithRecords } from '@models';
import {
  useDeletePriceListRecordsMutation,
  useGetAllPriceListsQuery,
  useGetAllProductsQuery,
  useUpsertPriceListRecordsMutation,
} from '@services';
import { addErrorToLog } from '@store/slices/error-log';
import { showToast } from '@store/slices/notification';
import { setStateBasedPriceList } from '@store/slices/products';

/**
 * Simple interface for rows - contains the static properties as well as a dynamic key-value property for individual state prices.
 */
export interface Row {
  id: number;
  sku: string;
  name: string;
  price: number;
  [key: string]: number | string | null | undefined;
}

/**
 * Even simpler interface for CSV rows - just a collection of key-value pairs.
 * These are filtered and assigned correctly in applyCsvChanges().
 */
export type CsvRow = { [key: string]: any };

const StateBasedPricing: FC = () => {
  const [isCsvDownloadModalOpen, setCsvDownloadModalOpen] = useState(false);
  const { data: products, isFetching: productsIsFetching } = useGetAllProductsQuery();
  const { data: priceLists, isFetching: priceListsIsFetching } = useGetAllPriceListsQuery();
  const [rows, setRows] = useState<Row[]>([]);
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState(false);

  // Set rows whenever data is received from BigCommerce.
  useEffect(() => {
    if (!priceLists?.length) return;

    if (!products?.length) return;

    dispatch(setStateBasedPriceList(priceLists));
    const rows: Row[] = products.map((product) => {
      // State names and prices aren't known in advance, hence the dynamic type.
      const statePrices: { [key: string]: number | null | undefined } = {};

      priceLists?.forEach((priceList) => {
        const field = `${priceList.name.toLowerCase()}`;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const record = priceList.records.find(({ product_id }) => product_id === product.id);

        statePrices[field] = record?.price;
      });

      return {
        id: product.id,
        sku: product.sku,
        name: product.name,
        price: product.price,
        ...statePrices,
      };
    });

    setRows(rows);
  }, [priceLists, products]);

  const columns: GridColDef[] = [
    { field: 'sku', headerName: 'SKU' },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'price', headerName: 'Base Price', valueFormatter: ({ value }) => toCurrency(Number(value)) },
    // Dynamically add all states as columns.
    ...(priceLists?.map((priceList) => ({
      field: `${priceList.name.toLowerCase()}`, // E.g. `nsw`
      headerName: `${priceList.name} Price`, // E.g. `NSW Price`
      editable: true,
      valueFormatter: ({ value }: { value: GridCellValue }) => {
        if (!value) return;

        return toCurrency(Number(value));
      },
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        // Only allow numbers.
        const value = Number(params.props.value);

        let hasError = false;

        if (isNaN(value)) {
          hasError = true;
        }

        return { ...params.props, error: hasError };
      },
    })) || []),
  ];

  // Keep track of what cells have been edited so that they can be compared on save.
  const [editedCells, setEditedCells] = useState<{ [row: number]: string[] }>({});

  /**
   * Updates `rows` whenever the value of any cell changes to avoid being out of sync. Also updates `editedCells`.
   */
  const handleCellChange = (params: GridCellEditCommitParams) => {
    const newRows = [...rows];
    const index = rows.findIndex((row) => row.id === params.id);

    if (index >= 0 && Number(params.value) !== rows[index][params.field]) {
      const newEditedCells = editedCells;

      if (editedCells[params.id as number]) {
        newEditedCells[params.id as number].push(params.field);
      } else {
        newEditedCells[params.id as number] = [params.field];
      }

      setEditedCells(newEditedCells);

      newRows[index] = {
        ...newRows[index],
        // Allow an empty string but convert anything else to a number.
        // This is to prevent showing "0" in a cell that should just be empty.
        [params.field]: params.value === '' ? params.value : Number(params.value),
      };
    }

    setRows(newRows);
  };

  /**
   * Updates `rows` and `editedCells` based on uploaded CSV data.
   */
  const applyCsvChanges = (csvRows: CsvRow[]) => {
    const newRows = [...rows];
    const states = priceLists?.map((priceList) => `${priceList.name.toLowerCase()}`);

    states?.forEach((state) => {
      // The existing, unaltered list.
      const existingList = priceLists?.find((list) => list.name.toLowerCase() === state);

      // `existingList` should always exist but this condition is here just in case.
      if (!existingList) return;

      csvRows.forEach((row) => {
        let productId = 0;

        const index = rows.findIndex((r) => {
          if (r.sku === row.sku) {
            productId = r.id; // Assign product ID from here instead of doing a separate lookup.
            return true;
          }

          return false;
        });

        if (index >= 0 && Number(row[state]) !== rows[index][state]) {
          const newEditedCells = editedCells;
          const existingRecord = existingList.records.find((record) => record.product_id === productId);

          if (row[state] !== '' || (existingRecord?.price && row[state] === '')) {
            if (editedCells[productId]) {
              newEditedCells[productId].push(state);
            } else {
              newEditedCells[productId] = [state];
            }

            setEditedCells(newEditedCells);

            newRows[index] = {
              ...newRows[index],
              // Allow an empty string but convert anything else to a number.
              // This is to prevent showing "0" in a cell that should just be empty.
              [state]: row[state] === '' ? row[state] : Number(row[state]),
            };
          }
        }
      });
    });

    setRows(newRows);
  };

  const dispatch = useAppDispatch();

  const [upsertPriceListRecords, { isLoading: upsertIsLoading }] = useUpsertPriceListRecordsMutation();
  const [deletePriceListRecords, { isLoading: deleteIsLoading }] = useDeletePriceListRecordsMutation();

  const handleSave = async () => {
    const recordsToBeDeleted: { priceListId: number; recordIds: number[] }[] = [];

    // To differentiate between the original lists and the lists that may have been updated.
    const listsFromRows: PriceListWithRecords[] = [];

    const states = priceLists?.map((priceList) => `${priceList.name.toLowerCase()}`);

    rows.forEach((row) => {
      states?.forEach((state) => {
        // The existing, unaltered list.
        const existingList = priceLists?.find((list) => list.name.toLowerCase() === state);

        // `existingList` should always exist but this condition is here just in case.
        if (!existingList) return;

        const existingRecord = existingList.records.find((record) => record.product_id === row.id);

        // Check if there is an existing record but no corresponding row, i.e. it has been removed by the user.
        if (existingRecord && !row[state]) {
          const existingIndex = recordsToBeDeleted.findIndex((r) => r.priceListId === existingList.id);

          existingIndex >= 0
            ? recordsToBeDeleted[existingIndex].recordIds.push(existingRecord.variant_id as number)
            : recordsToBeDeleted.push({ priceListId: existingList.id, recordIds: [existingRecord.variant_id as number] });
        }

        if (row[state]) {
          const existingIndex = listsFromRows.findIndex((list) => list.name.toLowerCase() === state);

          const newRecord: Partial<PriceListRecord> = {
            currency: 'aud',
            price: row[state] as number,
            product_id: row.id,
            sku: row.sku,
          };

          listsFromRows[existingIndex]?.records?.length
            ? (listsFromRows[existingIndex].records = [...listsFromRows[existingIndex].records, newRecord])
            : listsFromRows.push({ ...existingList, records: [newRecord] });
        }
      });
    });

    const listsToUpsert: PriceListWithRecords[] = [];

    listsFromRows.forEach((list) => {
      const existingList = priceLists?.find((l) => l.id === list.id);

      let valuesHaveChanged = false;

      if (list.records.length !== existingList?.records.length) {
        valuesHaveChanged = true;
      } else {
        list.records.forEach((record) => {
          const existingRecord = existingList.records.find((r) => r.product_id === record.product_id);

          if (record.price !== existingRecord?.price) {
            valuesHaveChanged = true;
          }
        });
      }

      valuesHaveChanged && listsToUpsert.push(list);
    });

    if (recordsToBeDeleted.length) {
      deletePriceListRecords(recordsToBeDeleted)
        .unwrap()
        .then(() => {
          dispatch(
            showToast({
              message: 'Successfully deleted price list records.',
              type: 'success',
            }),
          );

          setEditedCells([]);
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: 'Failed to delete price list records.',
              type: 'error',
            }),
          );
        });
    }

    if (listsToUpsert.length) {
      upsertPriceListRecords(listsToUpsert)
        .unwrap()
        .then(() => {
          dispatch(
            showToast({
              message: 'Successfully upserted price list records.',
              type: 'success',
            }),
          );

          setEditedCells([]);
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: 'Failed to upsert price list records.',
              type: 'error',
            }),
          );
        });
    }
  };

  return deleteIsLoading || productsIsFetching || priceListsIsFetching || upsertIsLoading ? (
    <LoadingSpinner isOpen={deleteIsLoading || productsIsFetching || priceListsIsFetching || upsertIsLoading} />
  ) : (
    <Box>
      <Grid container spacing={2} sx={{ py: 2 }}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-start" marginTop={2}>
            <Button
              variant="contained"
              sx={{ mr: 2 }}
              onClick={() => setCsvDownloadModalOpen(true)}
              data-testid="state-pricing-download-csv-btn"
            >
              Download CSV
            </Button>
            <Button
              variant="contained"
              sx={{ mr: 2 }}
              onClick={() => setCsvUploadModalOpen(true)}
              data-testid="state-pricing-upload-csv-btn"
            >
              Upload CSV
            </Button>
            <LoadingButton
              variant="contained"
              loading={deleteIsLoading || productsIsFetching || priceListsIsFetching || upsertIsLoading}
              onClick={handleSave}
              data-testid="state-pricing-save-btn"
            >
              Save
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
      {rows?.length > 0 && (
        <>
          <StateBasedPricingCsvDownloadModal isOpen={isCsvDownloadModalOpen} onClose={() => setCsvDownloadModalOpen(false)} rows={rows} />
          <StateBasedPricingCsvUploadModal
            applyCsvChanges={applyCsvChanges}
            isOpen={csvUploadModalOpen}
            onClose={() => setCsvUploadModalOpen(false)}
          />
          <Box sx={{ height: 800, width: '100%' }}>
            <DataGrid
              sortingOrder={['desc', 'asc']}
              rows={rows}
              columns={columns}
              disableSelectionOnClick
              onCellEditCommit={handleCellChange}
              getCellClassName={(params: GridCellParams<number>) => {
                // Highlight the cell if it has been edited.
                return editedCells[params.id as number]?.includes(params.field) ? 'edited' : '';
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: '#faf2ff',
                  },
                },
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default StateBasedPricing;
